import type {
    WaybillBidMatchingLogViewModel,
    WaybillBidMatchingSettingsViewModel,
    WaybillMatchedBidPointSnapshotViewModel,
    WaybillMatchedDriverSnapshotViewModel,
} from "api";
import cn from "classnames";
import Loader from "components/shared/loader";
import { dateTimeToView } from "helpers/date";
import { bidStatuses } from "helpers/enums";
import { paths } from "helpers/paths";
import { enumLabel } from "helpers/system";
import React, { FC } from "react";
import { Link } from "react-router-dom";

interface WaybillMatchingHistoryProps {
    logs: WaybillBidMatchingLogViewModel[] | null;
    loading: boolean;
    error: boolean;
    reload: () => void;
}

const getDriverFullName = (driver?: WaybillMatchedDriverSnapshotViewModel): string => {
    if (!driver) {
        return "";
    }
    return [driver.lastName, driver.firstName, driver.patronymic].filter(Boolean).join(" ");
};

const getDriverDetails = (driver?: WaybillMatchedDriverSnapshotViewModel): string | null => {
    if (!driver) {
        return null;
    }
    const parts: string[] = [];
    if (driver.phoneNumber) {
        parts.push(`Тел.: ${driver.phoneNumber}`);
    }
    if (driver.inn) {
        parts.push(`ИНН: ${driver.inn}`);
    }
    const license = [driver.driverLicenseSeries, driver.driverLicenseNumber].filter(Boolean).join(" ");
    if (license) {
        parts.push(`ВУ: ${license}`);
    }
    return parts.length > 0 ? parts.join(", ") : null;
};

const renderPoint = (point?: WaybillMatchedBidPointSnapshotViewModel) => {
    if (!point) {
        return <span className="text-muted">—</span>;
    }
    const counterparty = point.counterparty;
    const hasData = counterparty?.name || counterparty?.inn || point.planEnterDateOffset || point.coordinates?.length;
    if (!hasData) {
        return <span className="text-muted">—</span>;
    }

    return (
        <div>
            {counterparty?.name && (
                <div>
                    <b>Контрагент:</b> {counterparty.name}
                    {counterparty.inn && (
                        <span className="text-muted small">
                            {" "}
                            (ИНН: {counterparty.inn}
                            {counterparty.kpp ? `, КПП: ${counterparty.kpp}` : ""})
                        </span>
                    )}
                </div>
            )}
            {point.planEnterDateOffset && (
                <div className="text-muted small">План въезда: {dateTimeToView(point.planEnterDateOffset)}</div>
            )}
            {point.coordinates && point.coordinates.length === 2 && (
                <div className="text-muted small">Координаты: {point.coordinates.join(", ")}</div>
            )}
        </div>
    );
};

const renderMatchingSettings = (settings?: WaybillBidMatchingSettingsViewModel) => {
    if (!settings) {
        return <span className="text-muted">—</span>;
    }

    const items: React.ReactNode[] = [];

    if (settings.useShippingOrderNumber || settings.shippingOrderNumber) {
        items.push(
            <div key="so">
                <span className="text-muted">ЭЗЗ:</span> {settings.shippingOrderNumber || "Включено"}
            </div>,
        );
    }

    if (settings.driverByLicenseOrInn) {
        items.push(
            <div key="driver-check" className="text-muted small">
                Поиск водителя по ВУ или ИНН
            </div>,
        );
    }

    if (settings.allowedBidStatuses && settings.allowedBidStatuses.length > 0) {
        items.push(
            <div key="statuses">
                <span className="text-muted">Разрешённые статусы:</span>{" "}
                {settings.allowedBidStatuses.map((status) => enumLabel(bidStatuses, status, status)).join(", ")}
            </div>,
        );
    }

    if (settings.createDatePeriod) {
        items.push(
            <div key="create-period" className="small">
                <span className="text-muted">Период создания:</span> {dateTimeToView(settings.createDatePeriod.from)} —{" "}
                {dateTimeToView(settings.createDatePeriod.to)}
            </div>,
        );
    }

    if (settings.loadPoint) {
        const radius = settings.loadPoint.matchingRadius;
        if (radius != null) {
            items.push(
                <div key="load-radius" className="small">
                    <span className="text-muted">Радиус погрузки:</span>{" "}
                    {radius >= 1000 ? `${radius / 1000} км` : `${radius} м`}
                </div>,
            );
        }
        if (settings.loadPoint.planEnterDatePeriod) {
            items.push(
                <div key="load-period" className="small">
                    <span className="text-muted">Период въезда (погрузка):</span>{" "}
                    {dateTimeToView(settings.loadPoint.planEnterDatePeriod.from)} —{" "}
                    {dateTimeToView(settings.loadPoint.planEnterDatePeriod.to)}
                </div>,
            );
        }
    }

    if (settings.unloadPoint) {
        const radius = settings.unloadPoint.matchingRadius;
        if (radius != null) {
            items.push(
                <div key="unload-radius" className="small">
                    <span className="text-muted">Радиус разгрузки:</span>{" "}
                    {radius >= 1000 ? `${radius / 1000} км` : `${radius} м`}
                </div>,
            );
        }
    }

    return items.length > 0 ? <>{items}</> : <span className="text-muted">—</span>;
};

export const WaybillMatchingHistory: FC<WaybillMatchingHistoryProps> = ({ logs, loading, error, reload }) => {
    if (loading) {
        return (
            <div className="py-3">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-warning d-flex justify-content-between align-items-center mb-0" role="alert">
                <span>Не удалось загрузить историю сопоставления.</span>
                <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>
                    Повторить запрос
                </button>
            </div>
        );
    }

    if (!logs || logs.length === 0) {
        return <div className="text-muted text-center py-2">Сопоставлений ещё не было</div>;
    }

    return (
        <div className="waybill-matching-history">
            {logs.map((log) => {
                const isLinked = Boolean(log.bidId);
                const isManual = Boolean(log.updatedBy?.displayName || log.updatedById);
                const userName =
                    log.updatedBy?.displayName || (log.updatedById ? `Пользователь #${log.updatedById}` : "");
                const driverFullName = getDriverFullName(log.bid?.driver);
                const driverDetails = getDriverDetails(log.bid?.driver);

                return (
                    <div key={log.id} className="card border mb-3 font-size-14">
                        <div className="card-header bg-light py-2 d-flex flex-wrap justify-content-between align-items-center">
                            <div className="d-flex align-items-center flex-wrap">
                                <span className={cn("badge mr-2", isLinked ? "badge-success" : "badge-secondary")}>
                                    {isLinked ? "Заявка привязана" : "Связь с заявкой удалена"}
                                </span>
                                <span className="font-weight-bold mr-3">{dateTimeToView(log.createdAt)}</span>
                                {log.bidId ? (
                                    <span>
                                        <Link to={paths.bidShow("bid", log.bidId)}>Заявка №{log.bidId}</Link>
                                    </span>
                                ) : null}
                            </div>
                            <div className="text-muted small">
                                {isManual ? (
                                    <span>
                                        Вручную: <span className="text-dark font-weight-bold">{userName}</span>
                                    </span>
                                ) : (
                                    <span>Автоматически</span>
                                )}
                            </div>
                        </div>

                        {log.bid && (
                            <div className="card-body py-2 px-3">
                                <div className="row font-size-13">
                                    <div className="col-12 col-md-4 mb-2">
                                        <div className="font-weight-bold text-muted small mb-1 text-uppercase">
                                            Заявка
                                        </div>
                                        <div>
                                            <span className="text-muted">Автомобиль:</span>{" "}
                                            <span className="font-weight-bold">{log.bid.car?.number || "—"}</span>
                                        </div>
                                        <div>
                                            <span className="text-muted">Водитель:</span>{" "}
                                            <span className="font-weight-bold">{driverFullName || "—"}</span>
                                            {driverDetails && <div className="text-muted small">{driverDetails}</div>}
                                        </div>
                                        {log.bid.counterparty && (
                                            <div>
                                                <span className="text-muted">Контрагент заявки:</span>{" "}
                                                <span className="font-weight-bold">
                                                    {log.bid.counterparty.name || "—"}
                                                </span>
                                                {(log.bid.counterparty.inn || log.bid.counterparty.kpp) && (
                                                    <div className="text-muted small">
                                                        {[
                                                            log.bid.counterparty.inn &&
                                                                `ИНН: ${log.bid.counterparty.inn}`,
                                                            log.bid.counterparty.kpp &&
                                                                `КПП: ${log.bid.counterparty.kpp}`,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(", ")}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div>
                                            <span className="text-muted">Статус:</span>{" "}
                                            <span>
                                                {log.bid.status
                                                    ? enumLabel(bidStatuses, log.bid.status, log.bid.status)
                                                    : "—"}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-muted">Дата создания:</span>{" "}
                                            <span>{log.bid.createdAt ? dateTimeToView(log.bid.createdAt) : "—"}</span>
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-4 mb-2">
                                        <div className="font-weight-bold text-muted small mb-1 text-uppercase">
                                            Точки маршрута
                                        </div>
                                        <div className="mb-2">
                                            <div className="font-weight-bold">Погрузка:</div>
                                            {renderPoint(log.bid.loadPoint)}
                                        </div>
                                        <div>
                                            <div className="font-weight-bold">Разгрузка:</div>
                                            {renderPoint(log.bid.unloadPoint)}
                                        </div>
                                    </div>

                                    <div className="col-12 col-md-4 mb-2">
                                        <div className="font-weight-bold text-muted small mb-1 text-uppercase">
                                            Параметры сопоставления
                                        </div>
                                        {renderMatchingSettings(log.bid.settings)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
